import { useMemo } from "react";
import { useSession } from "@/services/auth0/session";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { z } from "zod";

const API_URL = import.meta.env.VITE_API_URL;

// Tracking response types
export interface TrackingEvent {
  timestamp: string;
  status: string;
  description: string;
  location?: string;
  city?: string;
  province?: string;
  country?: string;
  source: string;
  metadata?: Record<string, unknown>;
}

export interface CarrierTracking {
  trackingNumber: string;
  carrier: string;
  carrierName?: string;
  carrierTrackingUrl?: string;
  serviceCode?: string;
}

export interface PackageInfo {
  number: number;
  trackingNumber: string;
  carrierTracking?: string;
  status: string;
  weight: string;
  dimensions: string;
  description?: string;
  isFragile: boolean;
  requiresCooling: boolean;
  declaredValue?: string;
}

export interface AnticipatedEvent {
  status: string;
  description: string;
  isAnticipated: true;
}

export interface EstimatedMilestone {
  type: "CARRIER_DROPOFF" | "CUSTOMER_DELIVERY";
  label: string;
  estimate: string | null;
  timeWindow: {
    start: string;
    end: string;
  } | null;
  placeholder: string;
  isComplete: boolean;
  completedAt: string | null;
}

export interface TrackingResponse {
  // Primary tracking
  trackingNumber: string;
  shipmentNumber: number;

  // Carrier tracking info
  carrierTracking: CarrierTracking | null;

  // Current status
  currentStatus: string;
  status: string; // Legacy field
  lastUpdated: string;

  // Shipment details
  shipmentDetails: {
    shipmentId: string;
    orderId?: string;
    orderNumber?: number;
    estimatedDelivery?: string;
    actualDelivery?: string;
    recipientName: string;
    recipientAddress: string;
    senderName: string;
    senderAddress?: string;
    packageCount: number;
    totalWeight: number;
  };

  // Tracking timeline
  trackingEvents: TrackingEvent[];

  // Anticipated events (future steps)
  anticipatedEvents?: AnticipatedEvent[];

  // Estimated milestone (ETA display)
  estimatedMilestone?: EstimatedMilestone;

  // Package details
  packages: PackageInfo[];

  // Metadata
  metadata: {
    correlationId: string;
    carrierId: string;
    isLocalDelivery: boolean;
    hasMultiplePieces: boolean;
    carrierHandoffs: number;
  };
}

// Schema for manual event creation
export const createTrackingEventSchema = z.object({
  trackingNumber: z.string().optional(),
  shipmentId: z.string().optional(),
  status: z.string().min(1, "Status is required"),
  description: z.string().min(1, "Description is required"),
  location: z.string().optional(),
  city: z.string().optional(),
  province: z.string().optional(),
  country: z.string().optional(),
  postalCode: z.string().optional(),
  source: z
    .enum([
      "MANUAL",
      "INTERNAL",
      "CARRIER_WEBHOOK",
      "TASK_COMPLETION",
      "AGGREGATOR_API",
    ])
    .default("MANUAL"),
});

export type CreateTrackingEventData = z.infer<typeof createTrackingEventSchema>;

/**
 * Get tracking information by tracking number
 * Accepts both EZX and carrier tracking numbers
 */
export function useTracking(trackingNumber: string | undefined) {
  const { token, user, roles, isLoading } = useSession();
  const session = useMemo(() => ({ token, key: crypto.randomUUID() }), [token]);
  return useQuery({
    queryKey: [
      "tracking",
      trackingNumber,
      "staff",
      user?.id,
      [...roles].sort(),
      session.key,
    ],
    queryFn: async ({ signal }) => {
      if (!trackingNumber) throw new Error("Tracking number is required");
      const { data } = await axios.get<TrackingResponse>(
        `${API_URL}/api/v2/staff/tracking/${encodeURIComponent(trackingNumber)}`,
        {
          headers: { Authorization: `Bearer ${session.token}` },
          signal,
        }
      );
      return data;
    },
    enabled: !!session.token && !!user?.id && !isLoading && !!trackingNumber,
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });
}

/**
 * Create a manual tracking event
 * For admin/support use to add tracking events
 */
export function useCreateTrackingEvent() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (eventData: CreateTrackingEventData) => {
      const { data } = await axios.post<TrackingEvent>(
        `${API_URL}/api/v2/tracking/events`,
        eventData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return data;
    },
    onSuccess: (_, variables) => {
      // Invalidate tracking query for this shipment
      if (variables.trackingNumber) {
        void queryClient.invalidateQueries({
          queryKey: ["tracking", variables.trackingNumber],
        });
      }
      // Also invalidate shipments list
      void queryClient.invalidateQueries({
        queryKey: ["shipments"],
      });
    },
  });
}
